// Anexo 2 — Atención Prehospitalaria (Fase 6). Bloques y decisiones en
// `bloquesAnexo2Datos.ts` (fuente: PDF páginas 281-282). El paciente de la
// atención prehospitalaria puede no estar identificado, por eso el bloque B
// captura sus propios datos y el PDF sale en modo `pares` (sin el encabezado
// MSP de institución/HCU, que este formulario no trae).
//
// FHIR (solo con consumidor real): `Observation` de signos vitales por toma de D
// (temperatura, FC, FR, SpO2; PA como panel sist/diast — alimentan la pestaña
// Signos Vitales) y un `Condition` por diagnóstico de F con texto. Todo lo demás
// queda como `Par[]` de respaldo en el PDF firmado.
import type { Observation } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react-hooks';
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import type { FormularioDiscretoProps } from './index';
import { ErrorGuardado } from './ErrorGuardado';
import { useGuardado } from './useGuardado';
import { LoteTransaccional } from './transaccionFhir';
import { Bloque, CamposSimples } from './camposSimples';
import type { CampoSimple, Valores } from './camposSimplesDatos';
import { codeDeDiagnostico, SelectorDiagnosticoCie10 } from './SelectorDiagnosticoCie10';
import { TablaConColumnas } from './uci/TablaConColumnas';
import { PA_DIASTOLICA_LOINC, PA_PANEL_LOINC, PA_SISTOLICA_LOINC, signoVitalPorClave, VITAL_SIGNS_CATEGORY } from './vitalSigns';
import {
  CAMPOS_ACOMPANANTE_ANEXO2,
  CAMPOS_AMBULANCIA_ANEXO2,
  CAMPOS_CUSTODIA_ANEXO2,
  CAMPOS_DESTINO_ANEXO2,
  CAMPOS_ESTABILIZACION_ANEXO2,
  CAMPOS_EXONERACION_ANEXO2,
  CAMPOS_GINECO_ANEXO2,
  CAMPOS_OBSERVACIONES_ANEXO2,
  CAMPOS_PARO_ANEXO2,
  CAMPOS_UNIDAD_ANEXO2,
  COLUMNAS_CONTRACCIONES_ANEXO2,
  COLUMNAS_DESCARGO_ANEXO2,
  COLUMNAS_PERSONAL_ANEXO2,
  FILAS_CONTRACCIONES_ANEXO2,
  FILAS_DESCARGO_ANEXO2,
  FILAS_EQUIPO_ANEXO2,
  FILAS_RADIO_ANEXO2,
  CAMPOS_EXAMEN_ANEXO2,
  CAMPOS_GENERALES_ANEXO2,
  CAMPOS_INTERROGATORIO_ANEXO2,
  CAMPOS_LESION_ANEXO2,
  CAMPOS_ORIGEN_TRAUMA_ANEXO2,
  CAMPOS_OTROS_TRAUMA_ANEXO2,
  CAMPOS_PUPILAS_ANEXO2,
  CAMPOS_TIPO_ATENCION_ANEXO2,
  CAMPOS_TOMA_ANEXO2,
  CAMPOS_TRANSITO_ANEXO2,
  COLUMNAS_MEDICACION_ANEXO2,
  estadoInicialAnexo2,
  FILAS_MEDICACION_ANEXO2,
  GRUPOS_PROCEDIMIENTOS_ANEXO2,
  MAX_LESIONES_ANEXO2,
  paresDeAnexo2,
  TITULOS_ANEXO2 as T,
  totalGlasgowAnexo2,
  type ClaveGrupoProcedimientos,
  type EstadoAnexo2,
} from './bloquesAnexo2Datos';

function numeroOIndefinido(texto: string | undefined): number | undefined {
  if (texto === undefined || texto.trim() === '') return undefined;
  const n = Number(texto);
  return Number.isFinite(n) ? n : undefined;
}

/** Fecha/hora de una toma: la hora impresa sobre la fecha de atención (o despacho, o hoy); sin hora, el momento de guardar. */
function momentoDeToma(hora: string | undefined, generales: Valores, ahora: Date): string {
  if (!hora) return ahora.toISOString();
  const fecha = generales.fechaAtencion || generales.fechaDespacho || ahora.toLocaleDateString('sv-SE');
  const momento = new Date(`${fecha}T${hora}`);
  return Number.isNaN(momento.getTime()) ? ahora.toISOString() : momento.toISOString();
}

type ClaveValores = { [K in keyof EstadoAnexo2]: EstadoAnexo2[K] extends Valores ? K : never }[keyof EstadoAnexo2];

export function FormularioAnexo2({ subject, encounter, onGuardado }: FormularioDiscretoProps): JSX.Element {
  const { t } = useTranslation();
  const medplum = useMedplum();
  const { guardando, error, guardar } = useGuardado();
  const [estado, setEstado] = useState<EstadoAnexo2>(() => estadoInicialAnexo2());

  const handleSubmit = (): Promise<void> =>
    guardar(async () => {
      const lote = new LoteTransaccional();
      const pares = paresDeAnexo2(estado);
      const ahora = new Date();
      const observaciones: Observation[] = [];
      for (const toma of estado.tomas) {
        const efectivo = momentoDeToma(toma.hora, estado.generales, ahora);
        const simples: [string, string][] = [
          ['temperatura', 'temperatura'],
          ['fc', 'fc'],
          ['fr', 'fr'],
          ['sato2', 'spo2'],
        ];
        for (const [claveSigno, claveCampo] of simples) {
          const valor = numeroOIndefinido(toma[claveCampo]);
          if (valor === undefined) continue;
          const signo = signoVitalPorClave(claveSigno);
          observaciones.push(
            lote.crear<Observation>({
              resourceType: 'Observation',
              status: 'final',
              category: [VITAL_SIGNS_CATEGORY],
              code: { coding: [{ system: 'http://loinc.org', code: signo.loinc, display: signo.etiqueta }] },
              subject,
              encounter,
              effectiveDateTime: efectivo,
              valueQuantity: { value: valor, unit: signo.unidad, system: 'http://unitsofmeasure.org', code: signo.unidadUcum },
            }),
          );
        }
        const sistolica = numeroOIndefinido(toma.paSistolica);
        const diastolica = numeroOIndefinido(toma.paDiastolica);
        if (sistolica !== undefined && diastolica !== undefined) {
          observaciones.push(
            lote.crear<Observation>({
              resourceType: 'Observation',
              status: 'final',
              category: [VITAL_SIGNS_CATEGORY],
              code: { coding: [{ system: 'http://loinc.org', code: PA_PANEL_LOINC, display: 'Presión arterial' }] },
              subject,
              encounter,
              effectiveDateTime: efectivo,
              component: [
                { code: { coding: [{ system: 'http://loinc.org', code: PA_SISTOLICA_LOINC, display: 'Sistólica' }] }, valueQuantity: { value: sistolica, unit: 'mmHg', system: 'http://unitsofmeasure.org', code: 'mm[Hg]' } },
                { code: { coding: [{ system: 'http://loinc.org', code: PA_DIASTOLICA_LOINC, display: 'Diastólica' }] }, valueQuantity: { value: diastolica, unit: 'mmHg', system: 'http://unitsofmeasure.org', code: 'mm[Hg]' } },
              ],
            }),
          );
        }
      }
      for (const d of estado.diagnosticos) {
        if (d.texto.trim() !== '') lote.crear({ resourceType: 'Condition', subject, encounter, code: codeDeDiagnostico(d), recordedDate: ahora.toISOString() });
      }
      await lote.enviar(medplum);
      onGuardado(pares);
    });

  function bloque(clave: ClaveValores, titulo: string, prefijo: string, lista: readonly CampoSimple[], columnas?: 2 | 3 | 4): JSX.Element {
    return (
      <Bloque titulo={titulo}>
        <CamposSimples prefijoId={`anexo2-${prefijo}`} campos={lista} valores={estado[clave]} columnas={columnas} onChange={(v) => setEstado({ ...estado, [clave]: v })} />
      </Bloque>
    );
  }

  const setToma = (i: number, v: Valores): void => setEstado({ ...estado, tomas: estado.tomas.map((x, j) => (j === i ? v : x)) });
  const setLesion = (i: number, v: Valores): void => setEstado({ ...estado, lesiones: estado.lesiones.map((x, j) => (j === i ? v : x)) });

  return (
    <div className="flex flex-col gap-6">
      <h3 className="text-base font-medium text-hce-ink">{t('hub.formulario.Anexo2')}</h3>
      <p className="text-xs text-hce-muted">{t('formularios.fAnexo2.nota')}</p>

      {bloque('ambulancia', T.ambulancia, 'amb', CAMPOS_AMBULANCIA_ANEXO2)}

      <Bloque titulo={T.generales}>
        <CamposSimples prefijoId="anexo2-tipo" campos={CAMPOS_TIPO_ATENCION_ANEXO2} valores={estado.tipoAtencion} columnas={4} onChange={(v) => setEstado({ ...estado, tipoAtencion: v })} />
        <CamposSimples prefijoId="anexo2-gen" campos={CAMPOS_GENERALES_ANEXO2} valores={estado.generales} columnas={4} onChange={(v) => setEstado({ ...estado, generales: v })} />
      </Bloque>

      {bloque('interrogatorio', T.interrogatorio, 'int', CAMPOS_INTERROGATORIO_ANEXO2, 4)}

      <Bloque titulo={T.constantes}>
        {estado.tomas.map((toma, i) => {
          const total = totalGlasgowAnexo2(toma);
          return (
            <div key={i} className="flex flex-col gap-2 rounded-md border border-border p-3">
              <span className="text-xs font-medium text-hce-muted">{`${t('formularios.fAnexo2.toma')} ${i + 1}`}</span>
              <CamposSimples prefijoId={`anexo2-toma${i + 1}`} campos={CAMPOS_TOMA_ANEXO2} valores={toma} columnas={4} onChange={(v) => setToma(i, v)} />
              <p className="text-sm text-hce-ink" data-testid={`anexo2-glasgow-total-${i + 1}`}>
                {`${t('formularios.fAnexo2.glasgowTotal')}: ${total === null ? '—' : `${total}/15`}`}
              </p>
            </div>
          );
        })}
        <CamposSimples prefijoId="anexo2-pup" campos={CAMPOS_PUPILAS_ANEXO2} valores={estado.pupilas} columnas={4} onChange={(v) => setEstado({ ...estado, pupilas: v })} />
      </Bloque>

      {bloque('examen', T.examen, 'ex', CAMPOS_EXAMEN_ANEXO2, 3)}

      <Bloque titulo={T.diagnostico}>
        {estado.diagnosticos.map((d, i) => (
          <SelectorDiagnosticoCie10 key={i} label={`${T.diagnostico} ${i + 1}`} value={d} onChange={(v) => setEstado({ ...estado, diagnosticos: estado.diagnosticos.map((x, j) => (j === i ? v : x)) })} />
        ))}
      </Bloque>

      <Bloque titulo={T.procedimientos}>
        {GRUPOS_PROCEDIMIENTOS_ANEXO2.map((g) => (
          <div key={g.clave} className="flex flex-col gap-2">
            <span className="text-xs font-medium text-hce-muted">{g.titulo}</span>
            <CamposSimples
              prefijoId={`anexo2-proc-${g.clave}`}
              campos={g.campos}
              valores={estado.procedimientos[g.clave]}
              columnas={g.columnas}
              onChange={(v) => setEstado({ ...estado, procedimientos: { ...estado.procedimientos, [g.clave as ClaveGrupoProcedimientos]: v } })}
            />
          </div>
        ))}
        <span className="text-xs font-medium text-hce-muted">{t('formularios.fAnexo2.medicacionTitulo')}</span>
        <TablaConColumnas filas={FILAS_MEDICACION_ANEXO2} columnas={COLUMNAS_MEDICACION_ANEXO2} estado={estado.medicacion} onChange={(m) => setEstado({ ...estado, medicacion: m })} />
      </Bloque>

      <Bloque titulo={T.trauma}>
        <CamposSimples prefijoId="anexo2-origen" campos={CAMPOS_ORIGEN_TRAUMA_ANEXO2} valores={estado.origenTrauma} columnas={4} onChange={(v) => setEstado({ ...estado, origenTrauma: v })} />
        <span className="text-xs font-medium text-hce-muted">{t('formularios.fAnexo2.transitoTitulo')}</span>
        <CamposSimples prefijoId="anexo2-transito" campos={CAMPOS_TRANSITO_ANEXO2} valores={estado.transito} columnas={4} onChange={(v) => setEstado({ ...estado, transito: v })} />
        <CamposSimples prefijoId="anexo2-otros-trauma" campos={CAMPOS_OTROS_TRAUMA_ANEXO2} valores={estado.otrosTrauma} columnas={4} onChange={(v) => setEstado({ ...estado, otrosTrauma: v })} />
        <span className="text-xs font-medium text-hce-muted">{t('formularios.fAnexo2.localizacionTitulo')}</span>
        <p className="text-xs text-hce-muted">{t('formularios.fAnexo2.localizacionNota')}</p>
        {estado.lesiones.map((l, i) => (
          <div key={i} className="flex items-end gap-2">
            <div className="flex-1">
              <CamposSimples prefijoId={`anexo2-lesion${i + 1}`} campos={CAMPOS_LESION_ANEXO2} valores={l} columnas={3} onChange={(v) => setLesion(i, v)} />
            </div>
            <Button type="button" variant="outline" onClick={() => setEstado({ ...estado, lesiones: estado.lesiones.filter((_, j) => j !== i) })}>
              {t('formularios.fAnexo2.quitarLesion')}
            </Button>
          </div>
        ))}
        {estado.lesiones.length < MAX_LESIONES_ANEXO2 && (
          <Button type="button" variant="outline" className="w-fit" onClick={() => setEstado({ ...estado, lesiones: [...estado.lesiones, { lesion: '', vista: '', zona: '' }] })}>
            {t('formularios.fAnexo2.agregarLesion')}
          </Button>
        )}
      </Bloque>

      <Bloque titulo={T.gineco}>
        <CamposSimples prefijoId="anexo2-gin" campos={CAMPOS_GINECO_ANEXO2} valores={estado.gineco} columnas={4} onChange={(v) => setEstado({ ...estado, gineco: v })} />
        <span className="text-xs font-medium text-hce-muted">{t('formularios.fAnexo2.contraccionesTitulo')}</span>
        <TablaConColumnas filas={FILAS_CONTRACCIONES_ANEXO2} columnas={COLUMNAS_CONTRACCIONES_ANEXO2} estado={estado.contracciones} onChange={(c) => setEstado({ ...estado, contracciones: c })} />
      </Bloque>

      {bloque('paro', T.paro, 'paro', CAMPOS_PARO_ANEXO2, 4)}
      {bloque('estabilizacion', T.estabilizacion, 'est', CAMPOS_ESTABILIZACION_ANEXO2, 4)}
      {bloque('custodia', T.custodia, 'cus', CAMPOS_CUSTODIA_ANEXO2, 3)}

      <Bloque titulo={T.descargo}>
        <TablaConColumnas filas={FILAS_DESCARGO_ANEXO2} columnas={COLUMNAS_DESCARGO_ANEXO2} estado={estado.descargo} onChange={(d) => setEstado({ ...estado, descargo: d })} />
      </Bloque>

      {bloque('observaciones', T.observaciones, 'obs', CAMPOS_OBSERVACIONES_ANEXO2)}
      {bloque('acompanante', T.acompanante, 'acomp', CAMPOS_ACOMPANANTE_ANEXO2, 4)}

      <Bloque titulo={T.equipo}>
        <TablaConColumnas filas={FILAS_EQUIPO_ANEXO2} columnas={COLUMNAS_PERSONAL_ANEXO2} estado={estado.equipo} onChange={(x) => setEstado({ ...estado, equipo: x })} />
      </Bloque>

      <Bloque titulo={T.radio}>
        <TablaConColumnas filas={FILAS_RADIO_ANEXO2} columnas={COLUMNAS_PERSONAL_ANEXO2} estado={estado.radio} onChange={(x) => setEstado({ ...estado, radio: x })} />
        <span className="text-xs font-medium text-hce-muted">{t('formularios.fAnexo2.unidadTitulo')}</span>
        <CamposSimples prefijoId="anexo2-uni" campos={CAMPOS_UNIDAD_ANEXO2} valores={estado.unidad} columnas={3} onChange={(v) => setEstado({ ...estado, unidad: v })} />
      </Bloque>

      {bloque('destino', T.destino, 'dest', CAMPOS_DESTINO_ANEXO2, 3)}
      {bloque('exoneracion', T.exoneracion, 'exo', CAMPOS_EXONERACION_ANEXO2, 3)}

      <ErrorGuardado error={error} />

      <Button type="button" className="w-fit" onClick={() => void handleSubmit()} disabled={guardando}>
        {t('formulario.guardar')}
      </Button>
    </div>
  );
}
